/**
 * Smart Retry Hook
 * Implements intelligent reconnection with exponential backoff
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { UseSmartRetryConfig } from '../config/retryConfig';

export interface UseSmartRetryReturn {
  retry: () => Promise<boolean>;
  reset: () => void;
  isRetrying: boolean;
  attemptCount: number;
  nextRetryIn: number;
  canRetry: boolean;
  lastAttemptTime: Date | null;
}

export const useSmartRetry = (
  retryFunction: () => Promise<boolean>,
  config: UseSmartRetryConfig
): UseSmartRetryReturn => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [nextRetryIn, setNextRetryIn] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState<Date | null>(null);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const canRetry = attemptCount < config.maxAttempts;

  // Calculate delay with exponential backoff
  const calculateDelay = useCallback((attempt: number): number => {
    const delay = Math.min(
      config.initialDelay * Math.pow(config.backoffMultiplier, attempt),
      config.maxDelay
    );
    return delay;
  }, [config]);

  // Cleanup timers
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Reset retry state
  const reset = useCallback(() => {
    cleanup();
    setIsRetrying(false);
    setAttemptCount(0);
    setNextRetryIn(0);
    setLastAttemptTime(null);
  }, [cleanup]);

  // Start countdown timer
  const startCountdown = useCallback((delay: number) => {
    setNextRetryIn(Math.ceil(delay / 1000));
    
    countdownRef.current = setInterval(() => {
      setNextRetryIn(prev => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Main retry function
  const retry = useCallback(async (): Promise<boolean> => {
    if (!canRetry || isRetrying) {
      return false;
    }

    try {
      setIsRetrying(true);
      setLastAttemptTime(new Date());
      
      const success = await retryFunction();
      
      if (success) {
        if (config.resetOnSuccess) {
          reset();
        } else {
          setIsRetrying(false);
        }
        return true;
      } else {
        // Attempt failed, schedule next retry if possible
        const newAttemptCount = attemptCount + 1;
        setAttemptCount(newAttemptCount);
        
        if (newAttemptCount < config.maxAttempts) {
          const delay = calculateDelay(newAttemptCount);
          startCountdown(delay);
          
          timeoutRef.current = setTimeout(() => {
            setIsRetrying(false);
            // Don't auto-retry here, let the component decide
          }, delay);
        } else {
          setIsRetrying(false);
        }
        
        return false;
      }
    } catch (error) {
      console.error('Retry attempt failed:', error);
      
      const newAttemptCount = attemptCount + 1;
      setAttemptCount(newAttemptCount);
      
      if (newAttemptCount < config.maxAttempts) {
        const delay = calculateDelay(newAttemptCount);
        startCountdown(delay);
        
        timeoutRef.current = setTimeout(() => {
          setIsRetrying(false);
        }, delay);
      } else {
        setIsRetrying(false);
      }
      
      return false;
    }
  }, [canRetry, isRetrying, attemptCount, retryFunction, config, reset, calculateDelay, startCountdown]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    retry,
    reset,
    isRetrying,
    attemptCount,
    nextRetryIn,
    canRetry,
    lastAttemptTime,
  };
};
